import { sessionStorageService } from '@/core/storage/storage.service';
import { ref } from 'vue';
import { CheckoutFactory } from '@/api/checkout/apis/checkout';
import { BasketViewModel, SetAddressRequest, AmountViewObject, LineItemViewObject } from '@/api/checkout';
import { checkoutHost } from '@/core/infrastructure/environment';
import { getCultureSettings } from '@/project/content/api/contentApi';
import { GiftCardDetailsModel } from '@/api/content';
import { ContactInformationSchemaType } from './contact-information.service';

export type BasketSummaryType = {
    lineItems: LineItemViewObject[];
    currency: string;
    calculatedTotal: AmountViewObject;
    subTotal: AmountViewObject;
}

export const basketKeys = {
    GiftCard: `${await getCultureSettings().then(settings => settings.culture)}giftcardBasketId`,
};

const giftCardEnabledPromise = getCultureSettings().then(settings => settings.commerceEnabled || settings.isMarketGiftCardOnly);
const culturePromise = getCultureSettings().then(settings => settings.culture);

export const clientBasket = ref<BasketViewModel>({
    id: '',
    currency: '',
    culture: '',
    isAllProductsReserved: false,
    isAllProductsSoldOut: false,
    isCompleted: false,
    lineItems: [],
    subTotal: {
        formatted: '',
        value: 0,
    },
    total: {
        formatted: '',
        value: 0,
    },
    vat: {
        formatted: '',
        value: 0,
    },
    deliveryAddress: null,
    deliveryPrice: null,
    paymentId: null,
    selectedDropPoint: null,
    selectedShippingMethod: null,
    webOrderNumber: null,
    subscribeToNewsletter: false,
});

const basketApi = CheckoutFactory({
    basePath: checkoutHost,
    isJsonMime: () => true,
});

export async function getBasket(): Promise<BasketViewModel> {
    const giftCardEnabled = await giftCardEnabledPromise;
    const culture = await culturePromise;

    if (!giftCardEnabled) {
        return clientBasket.value;
    }

    const basketId = sessionStorageService.getItem(basketKeys.GiftCard);
    if (!basketId) {
        return clientBasket.value;
    }

    basketApi.apiCheckoutGetBasketGet(basketId, undefined, culture).then((res) => {
        clientBasket.value = res.data.model!;
    });

    return clientBasket.value;
}

export async function formsPayloadLogging(calledFrom: string, object: any) {
    await basketApi.apiCheckoutLogDataPost(calledFrom, object);
}

export async function saveAddress(address: SetAddressRequest): Promise<BasketViewModel | void> {
    const basket = await basketApi.apiCheckoutUpdateAddressPost(address);
    if (basket.data.model) {
        clientBasket.value = basket.data.model;
        return clientBasket.value;
    }
}

export async function prepareCompleteCheckout(): Promise<BasketViewModel> {
    const basketId = sessionStorageService.getItem(basketKeys.GiftCard) || '';
    const basket = await basketApi.apiCheckoutPrepareCompleteCheckoutPost({ basketId });
    clientBasket.value = basket.data.model as BasketViewModel;
    return clientBasket.value;
}

export async function completeCheckout(paymentId: string): Promise<BasketViewModel | undefined> {
    try {
        const response = await basketApi.apiCheckoutCompleteCheckoutPost({ paymentId });
        if (response.data.model) {
            clientBasket.value = response.data.model;
            return response.data.model;
        }
    } catch (error) {
        console.log({ error });
    }
}

export const getBasketSummary = (): BasketSummaryType => {
    return {
        calculatedTotal: { value: clientBasket.value?.subTotal?.value, formatted: clientBasket.value?.subTotal?.value.toString() },
        currency: clientBasket.value.currency,
        lineItems: clientBasket.value.lineItems,
        subTotal: clientBasket.value.subTotal,
    };
};

export const createGiftCardBasket = async(giftcard: GiftCardDetailsModel) => {
    const culture = await culturePromise;

    const giftcardBasket = await basketApi.apiCheckoutCreateGiftCardBasketPost({
        lineItem: {
            quantity: 1,
            sku: giftcard.sku,
        },
        culture,
    });

    if(!giftcardBasket.data.isSuccess) {
        return;
    }
    clientBasket.value = giftcardBasket.data.model;

    sessionStorageService.setItem(basketKeys.GiftCard, giftcardBasket.data.model.id);
};

export const updateGiftCard = async(id: string, contactInformation: ContactInformationSchemaType) => {
    await basketApi.apiCheckoutUpdateAddressPost({
        basketId: id,
        city: contactInformation.city,
        email: contactInformation.email,
        firstName: contactInformation.firstName,
        lastName: contactInformation.lastName,
        phone: contactInformation.phone,
        postalCode: contactInformation.postalCode.toString(),
        street: contactInformation.street,
        subscribeToNewsletter: contactInformation.subscribeToNewsletter,
    });
};
